import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Newspaper, Trophy, TrendingUp, Users, Settings, LogOut, ExternalLink, Shield, Gamepad2, Mail, MessageSquare, LayoutTemplate, FileText, Flag } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/news", label: "News", icon: Newspaper },
  { to: "/admin/pages", label: "Pages", icon: FileText },
  { to: "/admin/comments", label: "Comments", icon: MessageSquare },
  { to: "/admin/reports", label: "Reports", icon: Flag },
  { to: "/admin/content", label: "Live Content", icon: Gamepad2 },
  { to: "/admin/tournaments", label: "Tournaments", icon: Trophy },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/seo", label: "SEO & Traffic", icon: TrendingUp },
  { to: "/admin/accounts", label: "Accounts", icon: Users },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[hsl(var(--background))]">
      <aside className="w-60 shrink-0 border-r border-white/10 flex flex-col fixed inset-y-0 left-0 bg-[hsl(var(--card))]/50">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
          <span className="font-head text-xl font-bold" style={{ color: "hsl(var(--accent))" }}>7</span>
          <span className="font-head text-base font-bold text-white">LEGENDARY</span>
          <span className="ml-auto"><Shield size={15} className="text-white/30" /></span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  isActive ? "bg-[hsl(var(--primary))] text-white" : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <n.icon size={17} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/60 hover:text-white hover:bg-white/5">
            <ExternalLink size={17} /> View Site
          </a>
          <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/60 hover:text-white hover:bg-white/5">
            <LogOut size={17} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 ml-60 min-w-0">
        <header className="h-16 border-b border-white/10 flex items-center justify-end px-6 gap-3 sticky top-0 bg-[hsl(var(--background))]/80 backdrop-blur-xl z-30">
          <div className="text-right">
            <div className="text-sm font-semibold text-white">{user?.name}</div>
            <div className="text-xs text-white/40">{user?.email}</div>
          </div>
          {user?.picture && <img src={user.picture} alt="" className="h-9 w-9 rounded-full" />}
        </header>
        <div className="p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
